import { useMemo, useCallback } from 'react';
import { useMutation, MutationOptions, useMutationState } from '@tanstack/react-query';
import {
  useCompletedMediaAsset,
  useMediaAssetPollingContext,
} from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { GeneratedMediaAsset, MediaAssetStatus, StandaloneMediaAsset, MediaAssetType } from '@/types/media';
import flatten from 'lodash/flatten';
import { useMediaAssetsInvalidator } from './useMediaAssetsLoader';
interface useMediaAssetGenerationProps
  extends Omit<
    MutationOptions<StandaloneMediaAsset | StandaloneMediaAsset[] | null | string, Error, any>,
    'mutationFn'
  > {
  mutationFn: (params?: any) => Promise<StandaloneMediaAsset | StandaloneMediaAsset[] | null | string>;
  mediaAssetType?: MediaAssetType;
}
export const useMediaAssetGeneration = (mutationOptions: useMediaAssetGenerationProps) => {
  const {
    failedMediaAssets,
    completedMediaAssets,
    addMediaAssetToPollingQueue,
    initializeFailedMediaAssets,
    setPollingQueue,
  } = useMediaAssetPollingContext();
  const invalidateMediaAssets = useMediaAssetsInvalidator(mutationOptions.mediaAssetType);

  const generateMutation = useMutation<StandaloneMediaAsset | StandaloneMediaAsset[] | null | string, Error, any>(
    mutationOptions
  );

  const handleGenerate = useCallback(
    (params?: any) => {
      return generateMutation.mutateAsync(params, {
        onSuccess: (data) => {
          if (!data) {
            return;
          }
          if (params?.onSuccess) {
            params.onSuccess(data);
          }
          if (typeof data === 'object') {
            addMediaAssetToPollingQueue(data);
          }
          invalidateMediaAssets();
        },
        onError: (error) => {
          console.error(error);
          if (params?.onError) {
            params.onError(error);
          }
        },
      });
    },
    [generateMutation, addMediaAssetToPollingQueue, invalidateMediaAssets]
  );

  const { mutationKey } = mutationOptions;

  const dataFromMutationState = useMutationState({
    filters: mutationKey ? { mutationKey } : undefined,
    select: (mutation) => mutation.state.data,
  });

  const flattenedData = useMemo(() => {
    if (!mutationKey) return;
    const filteredData = dataFromMutationState?.filter((item) => item !== undefined && item !== null);
    return filteredData?.length ? flatten(filteredData) : undefined;
  }, [dataFromMutationState, mutationKey]) as StandaloneMediaAsset | StandaloneMediaAsset[];

  const data = useMemo(() => flattenedData || generateMutation.data, [flattenedData, generateMutation.data]) as
    | StandaloneMediaAsset
    | StandaloneMediaAsset[];

  const { isLoading, mediaAsset, hasError } = useCompletedMediaAsset(data);

  const isError = !!generateMutation.error || hasError;
  const isGenerating = generateMutation.isPending || isLoading;

  const generatedMediaAsset = useMemo<GeneratedMediaAsset | GeneratedMediaAsset[] | null>(() => {
    if (Array.isArray(data)) {
      return data.map((assetFromData) => {
        const foundMediaAsset = Array.isArray(mediaAsset)
          ? mediaAsset.find((assetFromMediaAsset) => assetFromMediaAsset.id === assetFromData.id)
          : mediaAsset?.id === assetFromData.id
            ? mediaAsset
            : null;
        return foundMediaAsset?.status === MediaAssetStatus.DONE || foundMediaAsset?.status === MediaAssetStatus.FAILED
          ? foundMediaAsset
          : assetFromData;
      }) as GeneratedMediaAsset[];
    }
    if (
      (mediaAsset as GeneratedMediaAsset)?.status === MediaAssetStatus.DONE ||
      (mediaAsset as GeneratedMediaAsset)?.status === MediaAssetStatus.FAILED
    ) {
      return mediaAsset as GeneratedMediaAsset;
    }
    return data as GeneratedMediaAsset | null;
  }, [mediaAsset, data]);

  const resetGeneratedMediaAsset = useCallback(() => {
    generateMutation.reset();
  }, [generateMutation]);

  return {
    generatedMediaAsset,
    isError,
    isGenerating,
    handleGenerate,
    resetGeneratedMediaAsset,
    failedMediaAssets,
    completedMediaAssets,
    initializeFailedMediaAssets,
    setPollingQueue,
  };
};
